import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import http from '@/libs/http'
import { toErrorMessage } from '@/libs/errorToaster'
import { useLocalStorage } from '@vueuse/core'
import dayjs from 'dayjs'
import { cp } from '@/utils/tools'
import { useRoute } from 'vue-router'

// --- Types ---
type Graph = Record<string, any>

interface Modul {
	_id: string
	customName: string
	name: string
	serial: string
	sensors: Record<string, any>
	created: number // Timestamp in milliseconds
	modified: number // Timestamp in milliseconds
	lastLog: number // Timestamp in milliseconds
	alertInterval: number
	show: boolean
}
interface Hub {
	meta: Record<string, any>
	modules: Modul[]
	graphs: Graph[]
}
interface Comapp extends Record<string, any> {}

interface Location {
	meta: Record<string, any>
	comapps: Comapp[]
	hubs: Hub[]
	graphs: Graph[]
}
interface Project {
	meta: Record<string, any>
	locations: Location[]
	graphs: Graph[]
}

interface MegaList {
	projects?: Project[]
}

interface MegaMap {
	projects: Record<
		string,
		{
			meta: Record<string, any>
			locations: Record<
				string,
				{
					meta: Record<string, any>
					hubs: Record<
						string,
						{
							meta: Record<string, any>
							modules: Record<string, Modul>
							graphs: Record<string, Graph>
						}
					>
				}
			>
			graphs: Record<string, Graph>
			comapps: Record<string, Comapp>
		}
	>
}

// --- Initial Date Ranges ---
const startOfDay = dayjs().subtract(14, 'day').startOf('day').toISOString()
const endOfDay = dayjs().add(2, 'day').endOf('day').toISOString()

export const useBaseStore = defineStore('base', () => {
	const route = useRoute()

	// --- State ---
	const users = ref<any[]>([])
	const megalist = useLocalStorage<MegaList>('base', {} as MegaList, {
		writeDefaults: false,
		onError: () => {
			// QuotaExceededError: silently clear and continue
			try { localStorage.removeItem('base') } catch {}
		},
	})
	const projects = ref<Record<string, any>[]>([])

	const allStates = ref({
		projects: {} as Record<string, any>,
		locations: {} as Record<string, any>,
		hubs: {} as Record<string, any>,
		modules: {} as Record<string, any>,
		comapps: {} as Record<string, any>,
	})
	const chartInitRange = ref<[string, string]>([startOfDay, endOfDay])
	const loadingMap = ref<Record<string, boolean>>({})
	const errorMap = ref<Record<string, string>>({})
	let fetchStatesPromise: Promise<void> | null = null

	// --- HELPERS ---

	async function withLoading<T>(key: string, fn: () => Promise<T>): Promise<T> {
		if (loadingMap.value[key]) {
			throw new Error(`🟧 ${key} is already in progress (baseStore)`)
		}
		loadingMap.value[key] = true
		delete errorMap.value[key]
		try {
			return await fn()
		} catch (e: any) {
			if (e?.name === 'AbortError') {
				console.warn(`🟨 ${key} was aborted (baseStore)`)
				throw e
			}
			const message = toErrorMessage(e, 'Unknown error')
			console.error(`🟥 Error in ${key} (baseStore):`, e)
			errorMap.value[key] = message
			throw new Error(message)
		} finally {
			delete loadingMap.value[key]
		}
	}

	// --- Getters ---

	const megaMap = computed((): MegaMap => {
		const map: MegaMap = {
			projects: {},
		}
		for (const project of megalist.value.projects || []) {
			const projectId = project.meta._id
			map.projects[projectId] = {
				meta: project.meta,
				locations: {},
				graphs: {},
				comapps: {},
			}
			for (const location of project.locations) {
				const locationId = location.meta._id
				map.projects[projectId].locations[locationId] = {
					meta: location.meta,
					hubs: {},
				}
				for (const hub of location.hubs) {
					map.projects[projectId].locations[locationId].hubs[hub.meta.serial] = {
						meta: hub.meta,
						modules: hub.modules.reduce(
							(acc, module) => {
								acc[module._id] = module
								return acc
							},
							{} as Record<string, Modul>,
						),
						graphs: hub.graphs.reduce(
							(acc, graph) => {
								acc[graph._id] = graph
								return acc
							},
							{} as Record<string, Graph>,
						),
					}
				}
				for (const graph of location.graphs) {
					map.projects[projectId].graphs[graph._id] = graph
				}
			}
			for (const graph of project.graphs) {
				map.projects[projectId].graphs[graph._id] = graph
			}
		}
		return map
	})

	const getLocations = computed(() => {
		const locations: Location[] = []
		for (const project of megalist.value.projects || []) {
			for (const location of project.locations) {
				locations.push(location)
			}
		}
		return cp(locations)
	})
	const getLocationsMap = computed(() => {
		const locationsMap: Record<string, Location> = {}
		for (const location of getLocations.value) {
			locationsMap[location.meta._id] = location
		}
		return locationsMap
	})

	const getModules = computed(() => {
		const modules: Modul[] = []
		for (const project of megalist.value.projects || []) {
			for (const location of project.locations) {
				for (const hub of location.hubs) {
					modules.push(...hub.modules)
				}
			}
		}
		return cp(modules)
	})

	const getGraphs = computed(() => {
		const graphs: Graph[] = []
		for (const project of megalist.value.projects || []) {
			for (const location of project.locations) {
				for (const hub of location.hubs) {
					graphs.push(...hub.graphs)
				}
				graphs.push(...location.graphs)
			}
			graphs.push(...project.graphs)
		}
		return cp(graphs)
	})

	const getGraphsByProjecId = computed(() => {
		const result: Record<string, Graph[]> = {}
		getGraphs.value.forEach((graph) => {
			const projectId = graph.projectId || 'NO_PROJECT'
			if (!result[projectId]) result[projectId] = []
			result[projectId].push(graph)
		})
		//sort by name:
		Object.keys(result).forEach((key) => {
			result[key].sort((a, b) => {
				if (a.name < b.name) return -1
				if (a.name > b.name) return 1
				return 0
			})
		})
		return result
	})
	const getGraphsByLocationId = computed(() => {
		const result: Record<string, Graph[]> = {}
		getGraphs.value.forEach((graph) => {
			const locationId = graph.locationId || 'NO_LOCATION'
			if (!result[locationId]) result[locationId] = []
			result[locationId].push(graph)
		})
		//sort by name:
		Object.keys(result).forEach((key) => {
			result[key].sort((a, b) => {
				if (a.name < b.name) return -1
				if (a.name > b.name) return 1
				return 0
			})
		})
		return result
	})

	// --- GETTERS ---

	const routeNamesForPanel = [
		//
		'Location',
		'Hub',
		'Module',
		'Chart',
		// 'Project',
		// 'Hubs',
		// 'NewGraph',
	]
	const routeNamesForHeader = [
		//
		'Projects',
		'Admin',
		'AuthUserProfile',
		'Projects',
		'Project',
		'Locations',
		'Location',
		'Users',

		'Hubs',
		'Hub',
		'Module',
		'Chart',
		'Graphs',
		'Inventory',
		// 'NewGraph',
	]

	const getActiveIds = computed(() => {
		let locationId = (route.params.locationId as string) || null
		let hubId = (route.params.hubId as string) || null
		let projectId = (route.params.projectId as string) || null
		let comappId = (route.params.comappId as string) || null
		let moduleId = (route.params.moduleId as string) || null
		let graphId: string | null = null
		console.log('getActiveIds', { locationId, hubId, projectId, comappId, moduleId })
		if (moduleId) {
			hubId = hubId || findHubForModuleId(moduleId) || null
			locationId = findLocationForModuleId(moduleId) || null
			if (!locationId) ({ locationId, projectId } = findLocationAndProjectIdForGraphId(moduleId))
		}
		if (locationId && !projectId) projectId = findProjectForLocationId(locationId) || null
		if (hubId && !locationId && !projectId) {
			const fai = findAnyIds(hubId)
			console.log('fai', fai)
			locationId = fai.locationId
			projectId = fai.projectId
		}

		const _showPanel = routeNamesForPanel.includes(route.name as string)
		const _showHeader = routeNamesForHeader.includes(route.name as string)

		if (moduleId) {
			const isGraph = getGraphs.value.find((g) => g._id === moduleId) || null
			if (isGraph) {
				graphId = moduleId
				moduleId = null
			}
		}

		return { moduleId, graphId, hubId, locationId, projectId, comappId, _showPanel, _showHeader }
	})

	// --- Actions & Helpers ---
	function setRange(range: [string, string]) {
		chartInitRange.value = range
	}

	function findHubForModuleId(moduleId: string): string | null {
		for (const project of megalist.value.projects || []) {
			for (const location of project.locations) {
				for (const hub of location.hubs) {
					for (const module of hub.modules) {
						if (module._id === moduleId) {
							return hub.meta.serial
						}
					}
					for (const graph of hub.graphs) {
						if ((graph as any)._id === moduleId) {
							return hub.meta.serial
						}
					}
				}
				for (const graph of location.graphs) {
					if ((graph as any)._id === moduleId) {
						return null
					}
				}
			}
		}
		return null
	}

	function findLocationForModuleId(moduleId: string): string | null {
		for (const project of megalist.value.projects || []) {
			for (const location of project.locations) {
				for (const hub of location.hubs) {
					for (const module of hub.modules) {
						if (module._id === moduleId) {
							return location.meta._id
						}
					}
					for (const graph of hub.graphs) {
						if ((graph as any)._id === moduleId) {
							return location.meta._id
						}
					}
				}
				for (const graph of location.graphs) {
					if ((graph as any)._id === moduleId) {
						return location.meta._id
					}
				}
			}
		}
		return null
	}

	function findLocationAndProjectIdForGraphId(graphId: string): {
		locationId: string | null
		projectId: string | null
	} {
		const _grpByLoc = getGraphsByLocationId.value
		for (const locationId in _grpByLoc) {
			const graphs = _grpByLoc[locationId]
			for (const graph of graphs) {
				if (graph._id === graphId) {
					console.log(graph)
					return { locationId: graph.locationId, projectId: graph.projectId }
				}
			}
		}
		return { locationId: null, projectId: null }
	}

	function findProjectForLocationId(locationId: string): string | null {
		for (const project of megalist.value.projects || []) {
			for (const location of project.locations) {
				if (location.meta._id === locationId) {
					return project.meta._id
				}
			}
		}
		return null
	}
	function findProjectForHubId(hubId: string): string | null {
		for (const project of megalist.value.projects || []) {
			for (const location of project.locations) {
				for (const hub of location.hubs) {
					if (hub.meta.serial === hubId) {
						return project.meta._id
					}
				}
			}
		}
		return null
	}

	function findAnyIds(anyId: string): {
		locationId: string | null
		projectId: string | null
		hubId: string | null
		moduleId: string | null
		graphId: string | null
		comappId: string | null
	} {
		// hubId, locationId, projectId, moduleId, graphId
		let hubId: string | null = null
		let locationId: string | null = null
		let projectId: string | null = null
		let moduleId: string | null = null
		let graphId: string | null = null
		let comappId: string | null = null

		for (const project of megalist.value.projects || []) {
			if (project.meta._id === anyId) {
				projectId = project.meta._id
				return { locationId, projectId, hubId, moduleId, graphId, comappId }
			}
			for (const location of project.locations) {
				if (location.meta._id === anyId) {
					locationId = location.meta._id
					projectId = project.meta._id
					return { locationId, projectId, hubId, moduleId, graphId, comappId }
				}
				for (const hub of location.hubs) {
					if (hub.meta.serial === anyId || hub.meta._id === anyId) {
						hubId = hub.meta.serial || hub.meta._id
						locationId = location.meta._id
						projectId = project.meta._id
						return { locationId, projectId, hubId, moduleId, graphId, comappId }
					}
					for (const module of hub.modules) {
						if (module._id === anyId) {
							moduleId = module._id
							hubId = hub.meta.serial || hub.meta._id
							locationId = location.meta._id
							projectId = project.meta._id
							return { locationId, projectId, hubId, moduleId, graphId, comappId }
						}
					}
					for (const graph of hub.graphs) {
						if ((graph as any)._id === anyId) {
							graphId = (graph as any)._id
							hubId = hub.meta.serial || hub.meta._id
							locationId = location.meta._id
							projectId = project.meta._id
							return { locationId, projectId, hubId, moduleId, graphId, comappId }
						}
					}
				}
				for (const graph of location.graphs) {
					if ((graph as any)._id === anyId) {
						graphId = (graph as any)._id
						locationId = location.meta._id
						projectId = project.meta._id
						return { locationId, projectId, hubId, moduleId, graphId, comappId }
					}
				}
			}
		}
		return { locationId, projectId, hubId, moduleId, graphId, comappId }
	}

	async function initAdmin() {
		await fetchMegalist()
		await fetchStates()
		await fetchUsers()
	}

	async function initBasicUser() {
		await fetchMegalist()
		await fetchStates()
	}

	// async function fetchStates() {
	const fetchStates = async () => {
		if (fetchStatesPromise) return fetchStatesPromise
		fetchStatesPromise = withLoading('fetchStates', async () => {
			const resp = await http.getAuth(`/api/v1/general/allstates`)
			allStates.value = resp || {}
		})
		fetchStatesPromise.finally(() => {
			fetchStatesPromise = null
		})
		return fetchStatesPromise
	}

	const fetchMegalist = () =>
		withLoading('fetchMegalist', async () => {
			const resp = await http.getAuth(`/api/v1/megalist`)
			console.log('🟣', resp)
			megalist.value = resp || {}
			prepareBasicData()
		})

	function prepareBasicData() {
		projects.value = (megalist.value.projects || [])
			.map((p) => p.meta)
			.sort((a, b) => (a['projectNumber'] > b['projectNumber'] ? 1 : -1))
	}

	const fetchUsers = () =>
		withLoading('fetchUsers', async () => {
			const resp = await http.getAuth(`/api/v1/users`)
			users.value = resp.users || []
		})

	const isMegalistReady = computed(() => !!(megalist.value.projects && megalist.value.projects.length))
	const isMegalistLoading = computed(() => !!loadingMap.value['fetchMegalist'])

	// --- Expose ---
	return {
		users,
		megalist,
		megaMap,
		projects,
		allStates,
		chartInitRange,
		isMegalistReady,
		isMegalistLoading,

		getActiveIds,
		getLocations,
		getLocationsMap,
		getModules,
		getGraphs,
		getGraphsByLocationId,
		getGraphsByProjecId,

		loadingMap,
		errorMap,

		setRange,
		findLocationForModuleId,
		findProjectForLocationId,
		initAdmin,
		initBasicUser,
		fetchStates,
		fetchMegalist,
		prepareBasicData,
		fetchUsers,
	}
})
